import { Injectable, inject } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { BettingInsightsModel } from '@cds/betting-offer/betting-insights';
import { FixtureView, SplitFixture } from '@cds/betting-offer/domain-specific';
import { BetBuilderFixture } from '@cds/betting-offer/domain-specific/bet-builder';
import { FixtureState, ParticipantMapping } from '@cds/query-objects';
import { EnhancedFixtureViewGroupingConfiguration } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { firstValueFrom } from 'rxjs';

import { BettingOfferApi } from './betting-offer-api.service';

export interface FixtureViewModel {
    fixture: Fixture;
    splitFixtures: SplitFixture[];
    preCreatedOffer?: BetBuilderFixture;
    grouping: EnhancedFixtureViewGroupingConfiguration;
    bettingInsights?: BettingInsightsModel;
}

export interface FixtureViewModelOptions {
    id: string;
    excludeMarkets?: boolean;
    statsMode: string;
    includePrecreated?: boolean;
    isBettingInsightsEnabled?: boolean;
    includeRelatedFixtures?: boolean;
    marketGroupIds?: string;
    marketGroupItemIds?: string;
    participantMapping?: ParticipantMapping;
    dynamicOfferCategories?: string;
    state?: FixtureState;
}

@Injectable({ providedIn: 'root' })
export class BettingOfferService {
    private bettingOfferApi = inject(BettingOfferApi);
    private offerGroupingService = inject(OfferGroupingService);

    async getFixtureViewModel(options: FixtureViewModelOptions): Promise<FixtureViewModel | undefined> {
        const {
            id,
            excludeMarkets = false,
            statsMode,
            includePrecreated = false,
            isBettingInsightsEnabled = false,
            includeRelatedFixtures = false,
            marketGroupIds,
            marketGroupItemIds,
            participantMapping,
            dynamicOfferCategories,
            state,
        } = options;

        const fixtureView = await this.bettingOfferApi.getFixtureView({
            id,
            statsMode,
            excludeMarkets,
            includePrecreated,
            isBettingInsightsEnabled,
            marketGroupIds,
            includeRelatedFixtures,
            marketGroupItemIds,
            participantMapping,
            dynamicOfferCategories,
            state,
        });

        return this.createViewModel(fixtureView);
    }

    private async createViewModel(fixtureView?: FixtureView): Promise<FixtureViewModel | undefined> {
        if (!fixtureView?.fixture) {
            return;
        }
        const fixture = fixtureView.fixture;

        const grouping = await firstValueFrom(this.offerGroupingService.getFixtureGrouping(fixture.sport.id, fixtureView.groupingVersion));

        if (!grouping) {
            throw new Error('Unable to load fixture grouping');
        }

        return {
            fixture,
            splitFixtures: fixtureView.splitFixtures,
            preCreatedOffer: fixtureView.preCreatedOffer,
            grouping,
            bettingInsights: fixtureView.bettingInsights,
        };
    }
}
